"use client";
import { Api } from "@/Apis";
import { CommonSelect } from "@/Components";
import { useRouter } from "@/Components/AppProgressBar";
import { countryAwarenessService } from "@/Helpers/CountryAwarenessService";
import { useResponsive, useT } from "@/Hooks";
import { Icon } from "@iconify/react";
import {
  AutoComplete,
  Button,
  Col,
  Form,
  Input,
  Row,
  Select,
  Skeleton,
  Spin,
} from "antd";
import React, { FC, useEffect, useRef, useState } from "react";
import { useDebounceCallback } from "usehooks-ts";

interface SearchBoxForm {
  ProvinceId?: number;
  CityId?: number;
  Search?: string;
}

interface SearchBoxProps {
  searchPlaceholder?: string;
  submitText?: string;
}

type SearchItem = {
  Type: "Doctor" | "Center" | string;
  Name: string;
  Link: string;
};

const SearchBox: FC<SearchBoxProps> = ({ searchPlaceholder, submitText }) => {
  const [form] = Form.useForm<SearchBoxForm>();
  const formProvinceId = Form.useWatch("ProvinceId", form);
  const formCityId = Form.useWatch("CityId", form);

  const router = useRouter();
  const { isMobile } = useResponsive();
  const t = useT();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<any[]>([]);
  const [defaultCountryId, setDefaultCountryId] = useState<number>();
  const [isCountryResolved, setIsCountryResolved] = useState(false);

  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const loadDefaultCountryId = async () => {
      try {
        const { Result } = await Api.CommonSelect.getCountries({});
        const defaultCountry = (Result ?? []).find(
          (item) => item.Key === countryAwarenessService.defaultCountryIsoCode,
        );

        setDefaultCountryId(defaultCountry?.Value ?? undefined);
      } finally {
        setIsCountryResolved(true);
      }
    };

    void loadDefaultCountryId();
  }, []);

  const buildGroupedOptions = (items: SearchItem[]) => {
    const doctors = items.filter(
      (x) => (x.Type || "").toLowerCase() === "doctor",
    );
    const centers = items.filter(
      (x) => (x.Type || "").toLowerCase() === "center",
    );

    const mapItem = (item: SearchItem) => ({
      value: item.Name,
      label: (
        <div className="flex justify-between">
          <span>{item.Name}</span>
        </div>
      ),
      link: item.Link,
      type: item.Type,
    });

    const groups: any[] = [];
    if (doctors.length)
      groups.push({
        label: t("landing.searchBox.group.doctors", undefined, "Doctors"),
        options: doctors.map(mapItem),
      });
    if (centers.length)
      groups.push({
        label: t("landing.searchBox.group.centers", undefined, "Centers"),
        options: centers.map(mapItem),
      });
    return groups;
  };

  const doSearch = async (value: string) => {
    const q = (value || "").trim();

    if (q.length < 2) {
      setOptions([]);
      setLoading(false);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);

    try {
      const res = await Api.LandingHome.search(
        { ProvinceId: formProvinceId, CityId: formCityId, Search: q },
        controller.signal,
      );

      if (controller.signal.aborted) return;

      const list =
        res?.Success && res.Result ? (res.Result as SearchItem[]) : [];
      setOptions(buildGroupedOptions(list));
      setOpen(true);
    } catch (e: any) {
      if (e?.name !== "AbortError") setOptions([]);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  const debouncedSearch = useDebounceCallback(doSearch, 400);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  useEffect(() => {
    form.setFieldValue("CityId", undefined);
  }, [form, formProvinceId]);

  const handleSelect = (_value: string, option: any) => {
    const link = option?.link as string | undefined;
    if (!link) return;

    setOpen(false);

    if (link.startsWith("http://") || link.startsWith("https://")) {
      window.location.href = link;
    } else {
      router.push(link);
    }
  };

  const popupRender = (menu: React.ReactElement) => {
    if (!loading) return menu;

    return (
      <div style={{ padding: 12 }}>
        <Skeleton active paragraph={{ rows: 3 }} />
        <Skeleton active paragraph={{ rows: 2 }} />
      </div>
    );
  };

  return (
    <Form form={form}>
      <Row
        gutter={isMobile ? [10, 10] : [0, 0]}
        align="middle"
        className="w-100"
      >
        <Col xs={24} md={6}>
          <Form.Item name="ProvinceId" className="mb-0">
            {isCountryResolved && defaultCountryId ? (
              <CommonSelect
                sourceType="Common"
                methodName="Provinces"
                model={{ CountryId: defaultCountryId }}
                size="large"
                prefix={<Icon icon="solar:map-point-outline" />}
                placeholder={t(
                  "landing.searchBox.province",
                  undefined,
                  "Province",
                )}
              />
            ) : (
              <Select
                size="large"
                prefix={<Icon icon="solar:map-point-outline" />}
                placeholder={t(
                  "landing.searchBox.province",
                  undefined,
                  "Province",
                )}
                disabled
                loading={!isCountryResolved}
              />
            )}
          </Form.Item>
        </Col>

        {formProvinceId ? (
          <Col xs={24} md={6}>
            <Form.Item name="CityId" className="mb-0">
              <CommonSelect
                sourceType="Common"
                methodName="Cities"
                model={{ ProvinceId: formProvinceId }}
                size="large"
                prefix={<Icon icon="solar:map-point-outline" />}
                placeholder={t("landing.searchBox.city", undefined, "City")}
              />
            </Form.Item>
          </Col>
        ) : null}

        <Col xs={24} md={formProvinceId ? 8 : 14}>
          <Form.Item name="Search" className="mb-0">
            <AutoComplete
              open={open}
              onOpenChange={setOpen}
              options={options}
              popupRender={popupRender}
              showSearch={{ onSearch: (v) => debouncedSearch(v) }}
              onSelect={handleSelect}
              allowClear
              onClear={() => {
                setOptions([]);
                setOpen(false);
                abortRef.current?.abort();
              }}
            >
              <Input
                size="large"
                prefix={<Icon icon="solar:magnifer-outline" />}
                placeholder={
                  searchPlaceholder ??
                  t(
                    "landing.searchBox.searchPlaceholder",
                    undefined,
                    "Search providers, clinics, hospitals and more...",
                  )
                }
                suffix={loading ? <Spin size="small" /> : null}
              />
            </AutoComplete>
          </Form.Item>
        </Col>

        <Col xs={24} md={4}>
          <Button
            shape="round"
            size="large"
            type="primary"
            htmlType="submit"
            block
            icon={<Icon icon="solar:magnifer-outline" />}
          >
            {submitText ?? t("common.action.search", undefined, "Search")}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default SearchBox;
